import LogoIcon from "@/assets/myth-icon.svg?react";
import { StoplightIndicator } from "@/components/stoplight-indicator";
import { Card, CardContent } from "@/components/ui/card";
import { toLocaleString, formatNumber, shortenNumber } from "@/lib/utils";
import {
  DSContractState,
} from "@myth-finance/dualstake-ts-sdk";
import { useState } from "react";
import { cx } from "class-variance-authority";
import {
  ArrowRightLeft,
  ClockAlertIcon,
  InfoIcon,
  Landmark,
  PercentIcon,
  Sprout,
} from "lucide-react";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { HelpButton } from "@/components/help-button";
import { Button } from "@/components/ui/button";
import { twMerge } from "tailwind-merge";
import { FullAprBreakdown } from "@/hooks/use-apr-breakdown";
import { useFarmsAndAPRs } from "@/hooks/use-farms-and-aprs";
import { DSContractListingExtd } from "@/lib/types";

export function StakedCard({
  listing,
  className,
  cState,
  hideAPRInfo,
  showLogo,
}: {
  className?: string;
  cState: DSContractState;
  listing: DSContractListingExtd;
  hideAPRInfo?: true;
  showLogo?: true;
}) {
  const { data: farmsAndAprs } = useFarmsAndAPRs(cState ? [cState] : []);
  const aprBreakdown = farmsAndAprs?.get(cState.appId)?.apr;

  const { staked, asaBalance, asaDecimals, asaUnitName, lstName } =
    cState ?? {};
  if (!cState) return null;

  return (
    <Card className={cx("w-[350px] relative self-stretch", className)}>
      <CardContent className="flex flex-col items-center py-4 opacity-0 animate-fade-in-1">
        <div className="flex self-start w-full justify-between items-start">
          <div className="flex flex-col opacity-0 animate-fade-in-1">
            <div className="flex gap-2">
              <span className="text-3xl ">
                {shortenNumber(Number(staked) / 1e6)}
              </span>
              <span className="text-3xl font-light">ALGO</span>
            </div>
            <div className="flex text-sm justify-start -mt-1 text-muted-foreground">
              Staked
            </div>
          </div>
          {showLogo ? (
            <LogoIcon
              className={`absolute opacity-20 top-3 right-4 ${getHighlightClasses(
                cState.asaId,
                "text"
              )}`}
              style={{ width: 48, height: 48 }}
            />
          ) : listing.sunset ? null : (
            <StoplightIndicator
              online={cState.isOnline}
              eligible={cState.incentiveEligible}
              className="mt-1 pr-0"
              name={lstName}
              value={cState.staked}
              sunset={listing.sunset}
            />
          )}
        </div>

        <div className="my-4 w-full group">
          <div className="self-start flex flex-col w-full  items-center">
            {listing.sunset ? (
              <div className="">
                <StoplightIndicator
                  iconSize={36}
                  online={cState.isOnline}
                  eligible={cState.incentiveEligible}
                  className="pr-0 -my-1"
                  name={lstName}
                  value={cState.staked}
                  sunset={listing.sunset}
                />
              </div>
            ) : (
              <div
                className={cx(
                  "flex text-3xl flex-wrap w-fit justify-center relative",
                  aprBreakdown === null ? "invisible" : "visible",
                  cState.staked < BigInt(30_000 * 1e6) && !showLogo
                    ? "line-through text-muted-foreground"
                    : ""
                )}
              >
                {!aprBreakdown || hideAPRInfo ? null : (
                  <div className="absolute mr-2 top-[50%] translate-y-[-50%] right-[100%] text-muted-foreground text-sm flex items-center">
                    <AprCalcHelpButton
                      aprBreakdown={aprBreakdown}
                      cState={cState}
                    />
                  </div>
                )}
                <span>
                  {!aprBreakdown
                    ? ""
                    : formatNumber(
                        Number(aprBreakdown?.netBaseAprBps) +
                          Number(aprBreakdown?.farmAprBps),
                        2,
                        1
                      )}
                </span>
                <span className="font-light">%</span>{" "}
                <div
                  className={cx(
                    "text-foreground absolute ml-2 mt-[1px] top-[50%] translate-y-[-50%] left-[100%] text-sm flex items-center",
                    cState.staked < BigInt(30_000 * 1e6)
                      ? "text-muted-foreground"
                      : ""
                  )}
                >
                  APR
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="self-end flex flex-col w-full justify-end">
          <span className="text-muted-foreground self-end text-sm mr-[2px]">
            Paired
          </span>
          <div className="flex gap-2 text-3xl flex-wrap justify-end -mt-1">
            <span className="">
              {shortenNumber(Number(asaBalance) / 10 ** Number(asaDecimals))}
            </span>
            <span className="font-light">{asaUnitName}</span>
          </div>
        </div>
      </CardContent>
      <Landmark
        size={160}
        strokeWidth={1.5}
        className="absolute pointer-events-none z-0 top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] opacity-5"
      />
    </Card>
  );
}

function AprCalcHelpButton({
  aprBreakdown,
  cState,
}: {
  aprBreakdown: FullAprBreakdown;
  cState: DSContractState;
}) {
  const [expanded, setExpanded] = useState(false);
  const [rateSide, setRateSide] = useState(false);
  const textColor = getHighlightClasses(cState.asaId, "text");
  const switchRate = () => setRateSide((r) => !r);
  const totalFeePercentage =
    Number(cState.platformFeeBps + cState.noderunnerFeeBps) / 100;
  const hoverText = getHighlightClasses(cState.asaId, "group-hover:text");
  return (
    <HelpButton
      trigger={(size, className) => (
        <InfoIcon size={size} className={className} />
      )}
      triggerClassName={`opacity-100 group-hover:scale-110 ${hoverText}`}
      infoIcon={(size, className) => (
        <PercentIcon
          size={size}
          className={twMerge(className, "text-muted-foreground")}
        />
      )}
      title={`${cState.lstName} APR Breakdown`}
    >
      {cState.staked >= BigInt(30_000 * 1e6) ? null : (
        <div className="flex flex-col w-fit text-orange-500">
          <div className="flex ml-1 gap-2">
            <ClockAlertIcon />{" "}
            <div className="flex flex-col">
              <span className="font-bold">Status: Funding</span>
              <span className="font-bold">Stake requirement not met</span>
              <span>Rewards require 30,000+ ALGO</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col w-fit text-teal-500">
        <div className="flex ml-1 gap-2">
          <Landmark /> <span>Algorand Protocol Net APR:</span>{" "}
          {formatNumber(aprBreakdown.netBaseAprBps, 2)}%
        </div>
        <div className="self-end text-muted-foreground">
          Gross APR: {formatNumber(Number(aprBreakdown?.baseAprBps), 2)}%
        </div>
      </div>

      {!aprBreakdown.farmAprBps ? null : (
        <div
          className={`flex ml-1 justify-between items-center gap-2 ${textColor}`}
        >
          <div className="flex gap-2 items-center">
            <Sprout className={textColor} /> <span>{cState.lstName} Farm</span>
          </div>
          <span>Net APR: {formatNumber(aprBreakdown?.farmAprBps, 2)}%</span>
        </div>
      )}

      <div className="flex flex-col ml-1 pl-3 text-muted-foreground italic">
        <div className="-ml-2 mb-1">
          Projected {cState.asaUnitName} buying pressure:{" "}
        </div>
        <div className="text-foreground">
          {formatNumber(
            aprBreakdown.netCurrentBlockBonus *
              aprBreakdown.expectedYearlyBlocks
          )}{" "}
          ALGO / year
        </div>
      </div>

      {!expanded ? (
        <Button variant="link" onClick={() => setExpanded(true)}>
          Show calculations
        </Button>
      ) : (
        <>
          <div className="flex flex-col ml-1 pl-3 text-muted-foreground italic">
            <div className="-ml-2 mb-1">Protocol APR estimation based on:</div>
            <div className="flex gap-1 items-center">
              <span className="text-foreground">
                {rateSide
                  ? toLocaleString(
                      Number(aprBreakdown.overrideFarmAmount) /
                        1e6 /
                        (Number(aprBreakdown.overrideFarmAmountAlgo) /
                          10 ** cState.asaDecimals),
                      6
                    )
                  : toLocaleString(
                      Number(aprBreakdown.overrideFarmAmountAlgo) /
                        1e6 /
                        (Number(aprBreakdown.overrideFarmAmount) /
                          10 ** cState.asaDecimals),
                      6
                    )}
              </span>{" "}
              {rateSide
                ? `${cState.asaUnitName}/ALGO`
                : `ALGO/${cState.asaUnitName}`}{" "}
              rate
              <span className="text-primary link">
                <ArrowRightLeft onClick={switchRate} size={16} />
              </span>
            </div>
            <div>
              <span className="text-foreground">
                {formatNumber(aprBreakdown.avgRoundTime, 4, 2)} sec
              </span>{" "}
              average round time{" "}
            </div>
            <div>
              <span className="text-foreground">
                {toLocaleString(
                  (100 * Number(aprBreakdown.balance)) /
                    Number(aprBreakdown.onlineStake),
                  4
                )}
                %
              </span>{" "}
              fraction of online stake
            </div>
            <div>
              <span className="text-foreground">
                {Number(aprBreakdown.expectedYearlyBlocks)}
              </span>{" "}
              expected blocks per year{" "}
            </div>
            <div>
              <span className="text-foreground">
                {formatNumber(aprBreakdown.currentBlockBonus)} ALGO
              </span>{" "}
              block payouts{" "}
            </div>
            <div>
              <span className="text-foreground">
                {formatNumber(aprBreakdown.netCurrentBlockBonus)} ALGO
              </span>{" "}
              swaps after {totalFeePercentage}% fees{" "}
            </div>
          </div>

          {!aprBreakdown.farmAprBps ? null : (
            <div className="flex flex-col ml-1 pl-3 text-muted-foreground italic">
              <div className="-ml-2 mb-1">Farming APR estimation based on:</div>
              <div>
                <span className="text-foreground">Forward projection</span> of
                current farm
              </div>
              <div>
                <span className="text-foreground">Current</span> ALGO/
                {cState.asaUnitName} rate
              </div>
            </div>
          )}
        </>
      )}
    </HelpButton>
  );
}
